import dayjs from 'dayjs';
import React, { useEffect } from 'react';

import useStore from 'Hooks/useStore';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { calculateTotalPassengers } from 'Util/_HumanWritten/CapacityCalculationUtils';
import { devLog } from 'Util/_HumanWritten/ConsoleLogUtils';
import { BookingWizardData, passengerFaresByLeg } from '../../../BookingWizardData/BaseBookingWizardData';
import { overfullFareTypes } from '../fareAvailability';
import { getWizardDataPassengerTicketDependencies } from '../../../BookingWizardData/PassengerDetail';

export const useTicketDeselectionWhenPaxExceeds = (
	wizardData: BookingWizardData,
	oldWizardData: BookingWizardData | null,
	saveChanges: (newData: BookingWizardData) => void,
	selectedDepartingTrip?: TicketsTabTrip,
	selectedReturningTrip?: TicketsTabTrip,
) => {
	const { isStaff, can } = useStore();

	const isValid = (passengerCount: number, capacity: number, departureDateTime: Date) => {
		if (can('OverbookInBookingWizard')) {
			return true; // may overbook, so no validation required.
		}

		if (passengerCount <= capacity) {
			return true; // pax can fit
		}

		// At this point, no space for passengers

		if (!isStaff) {
			return false; // visitor/customer cannot overbook
		}

		// Staff can only overbook if trip is in the past
		const isBeforeNow = dayjs(departureDateTime).isBefore(dayjs());
		return isBeforeNow;
	};

	const passengerTicketDependencies = React.useMemo(
		() => getWizardDataPassengerTicketDependencies(wizardData),
		[wizardData],
	);

	/*
		Who is in which fare type, on each leg, as one value for the dependency list.

		The counts above only move when a passenger is added or removed, and a passenger added to a
		sailing arrives with no fare type — the party's is handed to them a render later. Without this
		the check below ran while the new passenger counted against nothing, and never again.
	*/
	const fareTypeKey = [
		...passengerFaresByLeg(wizardData, 'departure').map(x => x.passengerClassId ?? ''),
		'|',
		...passengerFaresByLeg(wizardData, 'return').map(x => x.passengerClassId ?? ''),
	].join(',');

	/**
	 * Whether every fare type the booking uses on this sailing can take the passengers in it.
	 *
	 * <p>The same rule as the sailing as a whole, applied per fare type: a sailing with room can
	 * still have one place left in Premium and three passengers in it. `remaining` already hands back
	 * a booking's own places when it is being edited, so an edit is not refused its own sailing.</p>
	 */
	const fareTypesFit = (trip: TicketsTabTrip, leg: 'departure' | 'return') =>
		overfullFareTypes(
			trip.capacityTags,
			passengerFaresByLeg(wizardData, leg).map(x => x.passengerClassId),
		).every(overfull => isValid(overfull.count, overfull.remaining, trip.departureDateTime));

	useEffect(() => {
		const newData = new BookingWizardData({ ...wizardData });
		const paxCount = calculateTotalPassengers(newData);

		let changeDetected = false;
		// TODO : Having multiple hooks that uses saveChanges might lead to tricky debugging in the future,
		//        because other hooks can override the saveChanges state to be updated
		//
		// i.e. firstHook calls saveChanges, and secondHook calls saveChanges, the secondHook will take priority
		// since it is later in the hook cycle process... especially since it doesn't use the current state in
		// callback i.e setState(currentState => { /* update based on currentState */ })

		if (selectedDepartingTrip) {
			if (newData.departureTicketId !== '') {
				const paxSpaceLeft = selectedDepartingTrip.passengerSpacesAvailable;
				if (
					!isValid(paxCount, paxSpaceLeft, selectedDepartingTrip.departureDateTime) ||
					!fareTypesFit(selectedDepartingTrip, 'departure')
				) {
					changeDetected = true;
					newData.departureTicketId = '';
					newData.departingJourney = undefined;
					devLog('DEV0010: Deselect departing ticket');
				}
			}
		}

		if (selectedReturningTrip) {
			if (newData.returningTicketId !== '') {
				const paxSpaceLeft = selectedReturningTrip.passengerSpacesAvailable;
				if (
					!isValid(paxCount, paxSpaceLeft, selectedReturningTrip.departureDateTime) ||
					!fareTypesFit(selectedReturningTrip, 'return')
				) {
					console.error('selected returningTicketId is invalid');
					changeDetected = true;
					newData.returningTicketId = '';
					newData.returningJourney = undefined;
					devLog('DEV0011: Deselect returning ticket');
				}
			}
		}

		// Saving the changes to the wizard data so that the cart is in sync.
		if (changeDetected) {
			saveChanges(newData);
		}
	}, [
		selectedDepartingTrip,
		selectedReturningTrip,
		wizardData.fromLocationId,
		wizardData.toLocationId,
		wizardData.departureTicketId,
		wizardData.returningTicketId,
		...passengerTicketDependencies,
		fareTypeKey,
	]);
};
